import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AppInput from '../Forms/Inputs/AppInput';
import SelectInput from '../Forms/Inputs/SelectInput';
import { SelectOptions } from '../Forms/Inputs/SelectOption';
import OnboardingCTA from './OnboardingCTA';
import { INDIAN_STATES } from '../../constant/constant';
import './onboarding.scss';

const genderOptions = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];

const personalDetailsSchema = yup.object({
  firstName: yup
    .string()
    .trim()
    .matches(/^[a-zA-Z ]*$/, 'Special characters are not allowed')
    .max(30, 'First name must be at most 30 characters')
    .min(1, 'First name must be at least 1 character')
    .required('First name is required'),
  lastName: yup
    .string()
    .trim()
    .matches(/^[a-zA-Z ]*$/, 'Special characters are not allowed')
    .max(30, 'Last name must be at most 30 characters')
    .required('Last name is required'),
  gender: yup.string().required('Gender is required'),
  dateOfBirth: yup
    .string()
    .required('Date of birth is required')
    .test('valid-date', 'Invalid date', (value) => {
      if (!value) return true;
      const parts = value.split('-');
      if (parts.length !== 3) return false;
      const [dd, mm, yyyy] = parts;
      return dd.length === 2 && mm.length === 2 && yyyy.length === 4;
    }),
  whatsappNumber: yup
    .string()
    .test(
      'whatsappNumber',
      'Whatsapp Number must be 10 digits',
      function (value) {
        if (value && value.length > 0) {
          return /^[0-9]{10}$/.test(value);
        }
        return true;
      }
    ),
  city: yup.string().required('City is required'),
  pin: yup
    .string()
    .matches(/^[0-9]+$/, 'Enter valid pincode')
    .test('is-six-digits', 'Enter valid pincode', (value) => {
      if (value) {
        return value.length === 6;
      }
      return true;
    })
    .required('PIN code is required'),
  currentAddress: yup.string().required('Current Address is required'),
});

// Normalize state names from India Post API to match INDIAN_STATES list
const normalizeStateName = (apiState) => {
  const map = {
    'andaman and nicobar islands': 'Andaman Nicobar',
    'andaman & nicobar islands': 'Andaman Nicobar',
    'jammu and kashmir': 'Jammu Kashmir',
    'jammu & kashmir': 'Jammu Kashmir',
    'pondicherry': 'Puducherry',
    'daman and diu': 'Dadra and Nagar Haveli and Daman and Diu',
    'dadra and nagar haveli': 'Dadra and Nagar Haveli and Daman and Diu',
  };
  return map[apiState.toLowerCase()] || apiState;
};

// Fetch city and state for a 6-digit pincode from India Post API
const fetchPincodeLocation = async (pin) => {
  const response = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
  const data = await response.json();
  if (data[0]?.Status === 'Success' && data[0]?.PostOffice?.length > 0) {
    const postOffice = data[0].PostOffice[0];
    return {
      city: postOffice.District || postOffice.Block || '',
      state: normalizeStateName(postOffice.State || ''),
    };
  }
  return null;
};

const PersonalDetailsPage = ({ onNext, onPrevious, profileData }) => {
  const [selectedGender, setSelectedGender] = useState('');
  const [sameAsPhone, setSameAsPhone] = useState(false);
  const [dateValues, setDateValues] = useState({ dd: '', mm: '', yyyy: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isPincodeLoading, setIsPincodeLoading] = useState(false);
  const progressLineRef = useRef(null);
  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    reset,
    trigger,
    formState: { errors }
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      emailAddress: '',
      phone: '',
      whatsappNumber: '',
      gender: '',
      dateOfBirth: '',
      currentAddress: '',
      pin: '',
      city: '',
      state: '',
    },
    resolver: yupResolver(personalDetailsSchema)
  });

  const phoneValue = watch('phone');

  useEffect(() => {
    // Set mounted flag
    isMountedRef.current = true;
    
    return () => {
      // Cleanup: mark as unmounted and clear any pending timeouts
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (profileData && isMountedRef.current) {
      // Parse date of birth
      let formattedDate = '';
      if (profileData?.dateOfBirth) {
        const dateOnly = profileData.dateOfBirth.toString().split(' ')[0].split('T')[0];
        const dateParts = dateOnly.split('-');

        if (dateParts.length === 3) {
          let dd, mm, yyyy;
          if (dateParts[0].length === 4) {
            yyyy = dateParts[0];
            mm = dateParts[1].padStart(2, '0');
            dd = dateParts[2].padStart(2, '0');
          } else {
            dd = dateParts[0].padStart(2, '0');
            mm = dateParts[1].padStart(2, '0');
            yyyy = dateParts[2];
          }

          setDateValues({ dd, mm, yyyy });
          formattedDate = `${dd}-${mm}-${yyyy}`;
        }
      }

      // Use reset() to populate all form fields at once — ensures both
      // internal form state and DOM inputs are updated reliably on first load
      reset({
        firstName: profileData?.firstName || '',
        lastName: profileData?.lastName || '',
        emailAddress: profileData?.emailAddress || '',
        phone: profileData?.phone?.replace('+91-', '') || '',
        whatsappNumber: profileData?.whatsappNumber || '',
        gender: profileData?.gender || '',
        currentAddress: profileData?.currentAddress || '',
        pin: profileData?.pin || '',
        city: profileData?.city || '',
        state: profileData?.state || '',
        dateOfBirth: formattedDate
      });

      if (profileData?.gender) {
        setSelectedGender(profileData.gender);
      }

      // If pin is pre-filled but city is missing, auto-fetch location from pincode API
      if (profileData?.pin?.length === 6 && !profileData?.city) {
        fetchPincodeLocation(profileData.pin)
          .then((result) => {
            if (result && isMountedRef.current) {
              if (result.city) setValue('city', result.city);
              if (result.state) setValue('state', result.state);
            }
          })
          .catch(() => {});
      }
    }
  }, [profileData, reset, setValue]);

  useEffect(() => {
    if (sameAsPhone && phoneValue) {
      setValue('whatsappNumber', phoneValue);
    }
  }, [sameAsPhone, phoneValue, setValue]);

  useEffect(() => {
    if (progressLineRef.current) {
      // Step 1 (Personal Details) - 0% progress (just started)
      const progressWidth = '69px';
      progressLineRef.current.style.setProperty('--progress-width', progressWidth);
    }
  }, []);

  const handleDateChange = (field, value) => {
    const newDateValues = { ...dateValues, [field]: value };
    setDateValues(newDateValues);

    if (newDateValues.dd && newDateValues.mm && newDateValues.yyyy && newDateValues.yyyy.length === 4) {
      // Pad day and month to 2 digits for consistency
      const day = newDateValues.dd.padStart(2, '0');
      const month = newDateValues.mm.padStart(2, '0');
      const year = newDateValues.yyyy;

      // Store in DD-MM-YYYY format to match profile storage
      const formattedDate = `${day}-${month}-${year}`;
      setValue('dateOfBirth', formattedDate);
    } else {
      // Clear the form value if year is incomplete
      setValue('dateOfBirth', '');
    }
  };

  const handlePincodeChange = async (value) => {
    if (value.length === 6) {
      clearErrors('pin');
      setIsPincodeLoading(true);
      try {
        const result = await fetchPincodeLocation(value);
        if (isMountedRef.current) {
          if (result && result.city) {
            setValue('city', result.city);
            if (result.state) setValue('state', result.state);
          } else {
            setError('pin', { type: 'manual', message: 'Enter valid pincode' });
          }
        }
      } catch (_) {
        if (isMountedRef.current) {
          setError('pin', { type: 'manual', message: 'Enter valid pincode' });
        }
      } finally {
        if (isMountedRef.current) setIsPincodeLoading(false);
      }
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Call onNext without awaiting to ensure instant transition
      onNext(data);
      // Reset loading state after a short delay to allow button animation
      // Clear any existing timeout first
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
        timeoutRef.current = null;
      }, 100);
    } catch (error) {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    handleSubmit(onSubmit)();
  };

  return (
    <div className='onboarding-page'>
      <OnboardingCTA 
        onButtonClick={handleButtonClick} 
        buttonLabel='Submit & Continue' 
        variant='form'
        isLoading={isLoading}
      />

      <div className='onboarding-header'>
        <div className='step-indicators'>
          <div className='step-indicator-wrapper'>
            <div className='step-progress-line' ref={progressLineRef}></div>
            <div className='step active'>1</div>
            <div className='step'>2</div>
            <div className='step'>3</div>

          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className='onboarding-form'>
       
        <h2 className='onboarding-title'>
        {onPrevious && (
          <button
            type='button'
            onClick={onPrevious}
            className='onboarding-back-arrow'
            aria-label='Go back'
          >
            <span className='material-symbols-outlined'>arrow_back</span>
          </button>
        )}
          Verify Your Details</h2>

        <div className='form-row-1'>
          <AppInput
          className='input-field-onboarding'
            label='First Name'
            placeholder='Enter your first name'
            type='text'
            name='firstName'
            register={register}
            control={control}
            errors={errors}
            required
          />
              <AppInput
          className='input-field-onboarding'
            label='Last Name'
            placeholder='Enter your last name'
            type='text'
            name='lastName'
            register={register}
            control={control}
            errors={errors}
            required
          />
          <AppInput
            
            label='Your email address'
            placeholder='johnathan.doe@gmail.com'
            type='email'
            name='emailAddress'
            register={register}
            control={control}
            errors={errors}
            required
            disabled
            className='email-input-disabled input-field-onboarding'
          />
            <AppInput
            label='Your Phone Number'
            placeholder='Enter you phone Number'
            type='text'
            name='phone'
            register={(name) => ({
              ...register(name, {
                onChange: (e) => {
                  let value = e.target.value.replace(/\D/g, '').substring(0, 10);
                  e.target.value = value;
                }
              })
            })}
            control={control}
            errors={errors}
            disabled
            className='phone-input-disabled input-field-onboarding'
          />
         
        </div>

        <div className='form-row-2'>
        <div className='whatsapp-input-wrapper whatsapp-field-wrapper'>
           <AppInput
              label='Whatsapp Number'
              placeholder='Enter your whatsapp Number'
              type='text'
              name='whatsappNumber'
              register={(name) => ({
                ...register(name, {
                  onChange: (e) => {
                    let value = e.target.value.replace(/\D/g, '').substring(0, 10);
                    e.target.value = value;
                  }
                })
              })}
              control={control}
              errors={errors}
              disabled={sameAsPhone}
              required
              className='input-field-onboarding'
            />
            <div className='checkbox-container'>
              <input
                type='checkbox'
                id='sameAsPhone'
                checked={sameAsPhone}
                onChange={(e) => setSameAsPhone(e.target.checked)}
              />
              <label htmlFor='sameAsPhone'>Same as phone number</label>
            </div>
           </div>
          <div className='gender-section'>
            <label className='section-label'>
              What's your gender? <span className='required'>*</span>
            </label>
            <div className='gender-options'>
              {genderOptions.map((option) => (
                <button
                  key={option}
                  type='button'
                  className={`gender-option ${
                    selectedGender === option ? 'selected' : ''
                  }`}
                  onClick={() => {
                    setSelectedGender(option);
                    setValue('gender', option);
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
            {errors.gender && (
              <span className='error-message'>{errors.gender.message}</span>
            )}
          </div>

          <div className='dob-section'>
            <label className='section-label'>
              What's your Date of Birth? <span className='required'>*</span>
            </label>
            <div className='dob-inputs'>
              <input
                type='text'
                placeholder='DD'
                maxLength='2'
                value={dateValues.dd}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value === '' || (value.length <= 2 && parseInt(value) <= 31)) {
                    handleDateChange('dd', value);
                  }
                }}
                onBlur={() => {
                  if (dateValues.dd && parseInt(dateValues.dd) < 1) {
                    handleDateChange('dd', '');
                  }
                }}
                className='dob-input'
              />
              <span className='dob-separator'>/</span>
              <input
                type='text'
                placeholder='MM'
                maxLength='2'
                value={dateValues.mm}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value === '' || (value.length <= 2 && parseInt(value) <= 12)) {
                    handleDateChange('mm', value);
                  }
                }}
                onBlur={() => {
                  if (dateValues.mm && parseInt(dateValues.mm) < 1) {
                    handleDateChange('mm', '');
                  }
                }}
                className='dob-input'
              />
              <span className='dob-separator'>/</span>
              <input
                type='text'
                placeholder='YYYY'
                maxLength='4'
                value={dateValues.yyyy}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  handleDateChange('yyyy', value);
                }}
                className='dob-input'
              />
            </div>
            {errors.dateOfBirth && (
              <span className='error-message'>{errors.dateOfBirth.message}</span>
            )}
          </div>
        </div>

        <div className='address-section-wrapper'>
          <div className='address-fields-row'>
            <div className='pincode-input-wrapper'>
              <AppInput
                label='Pincode'
                placeholder='Enter 6-digit pincode'
                type='text'
                name='pin'
                register={(name) => ({
                  ...register(name, {
                    onChange: (e) => {
                      let value = e.target.value.replace(/\D/g, '').substring(0, 6);
                      e.target.value = value;
                      handlePincodeChange(value);
                    },
                    onBlur: () => trigger('pin')
                  })
                })}
                control={control}
                errors={errors}
                required
              />
              {isPincodeLoading && (
                <span className='pincode-loading-indicator'>Fetching location...</span>
              )}
            </div>
            <AppInput
              label='City'
              placeholder='Enter your city'
              type='text'
              name='city'
              register={register}
              control={control}
              errors={errors}
              required
            />
            <SelectInput
              label='State'
              placeholder='Select State'
              name='state'
              register={register}
              control={control}
              errors={errors}
            >
              <SelectOptions
                level={watch('state')}
                options={INDIAN_STATES}
              />
            </SelectInput>
          </div>
          <AppInput
            label='Location & Complete Address'
            placeholder='Enter Your Complete Address'
            type='text'
            name='currentAddress'
            register={register}
            control={control}
            errors={errors}
            required
          />
        </div>

      
      </form>
    </div>
  );
};

export default PersonalDetailsPage;
